import { closestCorners, DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef } from 'react';
import { DeliveryZoneComponent } from '../../components/DeliveryZoneComponent/DeliveryZoneComponent';
import deliveryZonesStore from '../../stores/deliveryZonesStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { DEBOUNCE_TIME, FORM_MODE } from 'src/constants/main';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { IDeliveryZone } from 'src/types/deliveryManagment';
import { dndAutoScrollOptionsDefault, dndOverlayAnimationDefault, DndSensors } from 'src/utils/dnd';
import { getTranslate } from 'src/utils/helpers';
import { findSubstring } from 'src/utils/parser';
import { ColorBox } from 'src/view/components/Controls/ColorBox/ColorBox';
import Icon from 'src/view/components/Controls/Icon/Icon';
import SearchInput from 'src/view/components/Controls/SearchInput/SearchInput';
import Portal from 'src/view/components/Portal/Portal';
import ScrollableList from 'src/view/components/ScrollableList/ScrollableList';
import './style.scss';

export const DeliveryZonesSidebarList = observer(() => {
  const wrapper = useRef<ScrollableList<IDeliveryZone> | null>(null);

  useEffect(() => {
    if (deliveryZonesStore.zone) {
      deliveryZonesStore.selectZone(deliveryZonesStore.zone);
    }
  }, []);

  useEffect(() => {
    const list = wrapper.current?.wrapper.current;
    const zoneItem = document.getElementById(`zone_${String(deliveryZonesStore.zone?.id || '')}`);

    if (!list || !zoneItem) return;
    // if (list.scrollTop > zoneItem.offsetTop || list.scrollTop + list.offsetHeight < zoneItem.offsetTop + zoneItem.clientHeight) {
    zoneItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // }
  }, [deliveryZonesStore.zone?.id]);

  const onChangeText = useCallback((search: string) => {
    deliveryZonesStore.setFilters({ search });
  }, []);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && over.id !== '0' && active.id !== over.id) {
      deliveryZonesStore.updateZonesPriority(Number(active.id), Number(over.id));
    }
    deliveryZonesStore.setDraggableZone(null);
  };

  const renderZone = (zone: IDeliveryZone | undefined, index: number) => {
    if (!zone) return null;
    return <DeliveryZoneComponent id={zone.id} key={zone.id} zone={zone} index={index} />;
  };

  return (
    <div className="delivery-zone-list">
      <div className="px-2 d-flex flex-row">
        <SearchInput
          customPlaceholder
          withResetIcon
          value={deliveryZonesStore.filters.search}
          loading={deliveryZonesStore.pagination.loading}
          debounceDelay={DEBOUNCE_TIME.LARGE}
          onChangeText={onChangeText}
          disabled={deliveryZonesStore.pagination.loading}
          placeholder={localeStore.t('branch_or_zone')}
          className="delivery-zone-list__search"
          inputClassName="delivery-zone-list__search-input"
        />

        <div className="ml-1">
          <ColorBox
            onSelect={() => deliveryZonesStore.selectDraft(null, FORM_MODE.EDIT)}
            className="cursor-pointer"
            color={theme.colors.bluelight}
            size={46}
          >
            <Icon name={Icons.Plus} size={16} className="color-mainblue " />
          </ColorBox>
        </div>
      </div>
      <div className="delivery-zone-list__container d-flex">
        <DndContext
          id="zones"
          autoScroll={dndAutoScrollOptionsDefault}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          sensors={DndSensors()}
          collisionDetection={closestCorners}
          onDragStart={({ active }) => deliveryZonesStore.setDraggableZone(Number(active.id))}
          onDragCancel={() => deliveryZonesStore.setDraggableZone(null)}
          onDragEnd={handleDragEnd}
        >
          <ScrollableList<IDeliveryZone>
            autoFetch
            fetch={deliveryZonesStore.fetchZones}
            list={deliveryZonesStore.pagination.data.filter(
              (zone) =>
                findSubstring(getTranslate(zone.name), deliveryZonesStore.filters.search || '') ||
                findSubstring(getTranslate(zone.branch?.name || ''), deliveryZonesStore.filters.search || '') ||
                findSubstring(zone.external_id || '', deliveryZonesStore.filters.search || '')
            )}
            loading={deliveryZonesStore.pagination.loading}
            refreshing={deliveryZonesStore.pagination.refreshing}
            current_page={deliveryZonesStore.pagination.current_page}
            last_page={deliveryZonesStore.pagination.last_page}
            scrollToTop={false}
            scrollbar
            scrollbarSize="normal"
            scrollbarClassName="delivery-zone-list__scroll"
            emptyListInFooter={false}
            emptyListText="no_more_zones"
            pages={deliveryZonesStore.pages}
            renderItem={(item, index) => renderZone(item, index)}
            ref={wrapper}
          />
          <Portal>
            <DragOverlay dropAnimation={dndOverlayAnimationDefault}>
              {deliveryZonesStore.draggableZone ? (
                <DeliveryZoneComponent id={0} zone={deliveryZonesStore.draggableZone} index={0} className="delivery-zone-item__drag_active" overlay />
              ) : null}
            </DragOverlay>
          </Portal>
        </DndContext>
      </div>
    </div>
  );
});
